import { useNavigate } from 'react-router-dom';
import { useMutation } from "@tanstack/react-query";
import { useState } from 'react';

const Join = () => {
	const API_URL = import.meta.env.VITE_API_URL;
	const navigate = useNavigate();
	const [isIdChecked, setIsIdChecked] = useState(false);
	const [form, setForm] = useState({id: "", password: "", passwordConfirm: "", parentName: "", parentEmail: "", parentPhone: "", childName: "", childBirth: "", childGender: "", agreeTerms: false, agreePrivacy: false});
	const [formErrors, setFormErrors] = useState({id: "", password: "", passwordConfirm: "", parentName: "", parentEmail: "", parentPhone: "", childName: "", childBirth: "", childGender: "", agreeTerms: "", agreePrivacy: ""});
	const [joinError, setJoinError] = useState("");	
	const [openTerms, setOpenTerms] = useState({terms: false, privacy: false});
	const isAllChecked = form.agreeTerms && form.agreePrivacy;

	const joinMutation = useMutation({
		mutationFn: async (data: { id: string, password: string, passwordConfirm: string, parentName: string, parentPhone: string, parentEmail: string, childName: string, childBirth: string, childGender: string }) => {
			const params = new URLSearchParams();
			Object.entries(data).forEach(([key, value]) => {
				params.append(key, value);
			});

			const response = await fetch(`${API_URL}/bbs/ajax.join.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
			});
			
			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if(!response.success) {
				switch (response.code) {
					case "INVALID_REQUEST":
						setJoinError("필수 필드값이 입력되지 않았습니다.");
						break;
					case "DUPLICATE_MEMBER":
						setIsIdChecked(false);
						setFormErrors((prev) => ({...prev, id: "이미 사용중인 아이디입니다."}));
						break;
					default :
						setJoinError("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if(!response.data) {
				setJoinError("알 수 없는 오류가 발생했습니다.");
				return;
			}

			if(response.data.memberId) {
				alert("회원가입이 완료되었습니다.");
				navigate("/member/login");
			}
		},
		onError: (error: any) => {
			setJoinError(error.message);
		}
	});

	const checkIdMutation = useMutation({
		mutationFn: async (data: { id: string }) => {
			const params = new URLSearchParams();
			Object.entries(data).forEach(([key, value]) => {
				params.append(key, value);
			});

			const response = await fetch(`${API_URL}/bbs/ajax.check_id.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
			});

			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if(!response.success) {
				setIsIdChecked(false);
				switch (response.code) {
					case "INVALID_REQUEST":
						setFormErrors((prev) => ({...prev, id: "아이디를 입력해주세요."}));
						break;
					case "DUPLICATE_MEMBER":
						setFormErrors((prev) => ({...prev, id: "이미 사용중인 아이디입니다."}));
						break;
					default:
						setFormErrors((prev) => ({...prev, id: "알 수 없는 오류가 발생했습니다."}));
						break;
				}
				return;
			}

			if(!response.data) {
				setFormErrors((prev) => ({...prev, id: "알 수 없는 오류가 발생했습니다."}));
				return;
			}

			if(response.data.memberAvailable) {
				setIsIdChecked(true);
				setFormErrors((prev) => ({ ...prev, id: "" }));
			}
		},
		onError: (error: any) => {
			setIsIdChecked(false);
			setFormErrors((prev) => ({...prev, id: error.message}));
		}
	});

	const validate = (name: string, value: any, form: any) => {
		let validValue = value;
		let errorMsg = "";

		if (name === "id") {
			if (!value.trim()) {
				errorMsg = "아이디를 입력해주세요.";
			} else if (/[^a-zA-Z0-9]/.test(value)) {
				errorMsg = "아이디는 영문, 숫자만 입력 가능합니다.";
			} else if (value.length < 3) {
				errorMsg = "아이디는 3자 이상 입력해주세요.";
			}
		}

		if (name === "password") {
			if (!value.trim()) {
				errorMsg = "비밀번호를 입력해주세요.";
			} else if (value.length < 4) {
				errorMsg = "비밀번호는 4자 이상 입력해주세요.";
			}
			validValue = value.replace(/\s/g, "");
		}

		if (name === "passwordConfirm") {
			if (!value.trim()) {
				errorMsg = "비밀번호 확인을 입력해주세요.";
			} else if (value !== form.password) {
				errorMsg = "비밀번호가 일치하지 않습니다.";
			}
		}

		if (name === "parentName") {
			validValue = value.replace(/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ]/g, "");
			if (!validValue.trim()) {
				errorMsg = "이름을 입력해주세요.";
			}
		}

		if (name === "parentEmail") {
			validValue = value.trim();
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(validValue)) {
				errorMsg = "올바른 이메일 형식이 아닙니다.";
			}
		}

		if (name === "parentPhone") {
			let numbers = value.replace(/[^0-9]/g, "");
			if (numbers.length < 4) {
				validValue = numbers;
			} else if (numbers.length < 8) {
				validValue = `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
			} else {
				validValue = `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
			}
			if (numbers.length < 10) {
				errorMsg = "올바른 연락처를 입력해주세요.";
			}
		}

		if (name === "childName") {
			validValue = value.replace(/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ]/g, "");
			if (!validValue.trim()) {
				errorMsg = "이름을 입력해주세요.";
			}
		}

		if (name === "childBirth") {
			let numbers = value.replace(/[^0-9]/g, "").slice(0, 8);
			if (numbers.length < 5) {
				validValue = numbers;
			} else if (numbers.length < 7) {
				validValue = `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
			} else {
				validValue = `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6)}`;
			}

			if (numbers.length === 8) {
				const year = Number(numbers.slice(0, 4));
				const month = Number(numbers.slice(4, 6));
				const day = Number(numbers.slice(6, 8));

				if (month < 1 || month > 12 || day < 1 || day > 31) {
					errorMsg = "올바른 생년월일을 입력해주세요.";
				}
			} else if (numbers.length > 0) {
        		errorMsg = "올바른 생년월일을 입력해주세요.";
    		}
		}

		return {
			value: validValue,
			error: errorMsg,
		};
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type, checked } = e.target;

		if (name === "id") setIsIdChecked(false);

		const inputValue = type === "checkbox" ? checked : value;

		const result = validate(name, inputValue, form);

		setJoinError("");
		setForm(prev => ({ ...prev, [name]: result.value }));
		setFormErrors(prev => ({ ...prev, [name]: result.error }));
	};

	const handleCheckId = () => {
		checkIdMutation.mutate({id: form.id});
	}

	const handleAllAgree = (e) => {
		const checked = e.target.checked;
		setForm((prev) => ({...prev, agreeTerms: checked, agreePrivacy: checked}));
	};

	const handleJoin = () => {
		const fields = ["id", "password", "passwordConfirm", "parentName", "parentPhone", "parentEmail", "childName", "childBirth"];
		const newErrors: any = {};
		const newForm: any = { ...form };

		fields.forEach((field) => {
			const result = validate(field, form[field], form);
			newErrors[field] = result.error;
			newForm[field] = result.value;
		});

		setFormErrors(newErrors);

		if (Object.values(newErrors).some((e) => e)) return;

		if (!isIdChecked) {
			setFormErrors((prev) => ({...prev, id: "아이디 중복확인을 해주세요."}));
			return;
		}

		joinMutation.mutate(newForm);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") handleJoin();
	};

	return (
		<section id="join" className="min-h-[80vh] flex items-center py-12 md:py-16 bg-gradient-to-br from-purple-600 to-blue-600">
			<div className="max-w-7xl mx-auto px-4 w-full">
				<div className="max-w-2xl mx-auto">
					<div className="text-center mb-10">
						<h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
							회원가입
						</h2>
						<p className="text-white/80 mt-2 text-sm">
							서비스를 이용하려면 회원가입해주세요
						</p>
					</div>
					<div className="bg-white/95 backdrop-blur rounded-2xl p-6 md:p-8 shadow-xl border border-white/20">
						<div className="space-y-5">
							<div>
								<label htmlFor="id" className="block text-sm font-semibold text-gray-700 mb-1">
									아이디
								</label>
								<div className="flex flex-col sm:flex-row gap-2">
									<input
										type="text"
										id="id"
										name="id"
										value={form.id}
										onChange={handleChange}
										disabled={joinMutation.isPending || isIdChecked}
										placeholder="아이디를 입력해주세요"
										className={`w-full sm:flex-1 px-4 py-3 rounded-lg border text-sm transition
											${formErrors.id
												? "border-red-500 focus:ring-red-500 focus:border-red-500"
												: "border-gray-300 focus:ring-purple-500 focus:border-purple-500"}
										`}
									/>
									<button
										type="button"
										onClick={handleCheckId}
										disabled={!form.id || joinMutation.isPending || isIdChecked}
										className={`w-full sm:w-auto px-4 py-3 rounded-lg text-sm font-semibold transition whitespace-nowrap
											${
												isIdChecked
													? "bg-green-500 text-white cursor-default"
													: !form.id || joinMutation.isPending
													? "bg-gray-200 text-gray-400 cursor-not-allowed"
													: "bg-purple-600 text-white hover:bg-purple-700"
											}
										`}
									>
										{isIdChecked ? "확인완료" : "중복확인"}
									</button>
								</div>
								{formErrors.id && (
									<p className="text-red-500 text-xs mt-1 min-h-[16px]">
										{formErrors.id}
									</p>
								)}
							</div>
							<div>
								<label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">
									비밀번호
								</label>
								<input
									type="password"
									id="password"
									name="password"
									value={form.password}
									onChange={handleChange}
									disabled={joinMutation.isPending}
									placeholder="비밀번호를 입력해주세요"
									className={`w-full px-4 py-3 rounded-lg border text-sm transition
      									${formErrors.password ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"}
							      	`}
								/>
								{formErrors.password && (
									<p className="text-red-500 text-xs mt-1 min-h-[16px]">
										{formErrors.password}
									</p>
								)}
							</div>
							<div>
								<label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">
									비밀번호 확인
								</label>
								<input
									type="password"
									id="passwordConfirm"
									name="passwordConfirm"
									value={form.passwordConfirm}
									onChange={handleChange}
									disabled={joinMutation.isPending}
									placeholder="비밀번호를 입력해주세요"
									className={`w-full px-4 py-3 rounded-lg border text-sm transition
      									${formErrors.passwordConfirm ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"}
							      	`}
								/>
								{formErrors.passwordConfirm && (
									<p className="text-red-500 text-xs mt-1 min-h-[16px]">
										{formErrors.passwordConfirm}
									</p>
								)}
							</div>
							<div className="grid md:grid-cols-2 gap-6">
								<div className="bg-gray-50 rounded-xl p-5 border">
									<div className="flex items-center gap-2 mb-3">
										<h3 className="text-sm font-semibold text-gray-500">
											부모 / 보호자
										</h3>
									</div>
									<div className="space-y-4">
										<input
											type="text"
											name="parentName"
											placeholder="이름을 입력해주세요."
											value={form.parentName}
											disabled={joinMutation.isPending}
											onChange={handleChange}
											className="w-full px-4 py-3 rounded-lg border text-sm"
										/>
										{formErrors.parentName && (
											<p className="text-red-500 text-xs mt-1 min-h-[16px]">
												{formErrors.parentName}
											</p>
										)}
										<input
											type="text"
											name="parentEmail"
											placeholder="이메일을 입력해주세요."
											value={form.parentEmail}
											disabled={joinMutation.isPending}
											onChange={handleChange}
											className="w-full px-4 py-3 rounded-lg border text-sm"
										/>
										{formErrors.parentEmail && (
											<p className="text-red-500 text-xs mt-1 min-h-[16px]">
												{formErrors.parentEmail}
											</p>
										)}
										<input
											type="text"
											name="parentPhone"
											placeholder="연락처를 입력해주세요."
											value={form.parentPhone}
											disabled={joinMutation.isPending}
											onChange={handleChange}
											className="w-full px-4 py-3 rounded-lg border text-sm"
										/>
										{formErrors.parentPhone && (
											<p className="text-red-500 text-xs mt-1 min-h-[16px]">
												{formErrors.parentPhone}
											</p>
										)}
									</div>
								</div>
								<div className="bg-gray-50 rounded-xl p-5 border">
									<div className="flex items-center gap-2 mb-3">
										<h3 className="text-sm font-semibold text-gray-500">
											자녀
										</h3>
									</div>
									<div className="space-y-4">
										<input
											type="text"
											name="childName"
											placeholder="이름을 입력해주세요."
											value={form.childName}
											disabled={joinMutation.isPending}
											onChange={handleChange}
											className="w-full px-4 py-3 rounded-lg border text-sm"
										/>
										{formErrors.childName && (
											<p className="text-red-500 text-xs mt-1 min-h-[16px]">
												{formErrors.childName}
											</p>
										)}
										<input
											type="text"
											name="childBirth"
											placeholder="생년월일을 입력해주세요."
											value={form.childBirth}
											disabled={joinMutation.isPending}
											onChange={handleChange}
											className="w-full px-4 py-3 rounded-lg border text-sm"
										/>
										{formErrors.childBirth && (
											<p className="text-red-500 text-xs mt-1 min-h-[16px]">
												{formErrors.childBirth}
											</p>
										)}
										<div className="w-full">
											<div className="flex w-full rounded-xl border overflow-hidden">
												<label className={`flex-1 text-center py-3 cursor-pointer transition font-medium
													${form.childGender === "M"
														? "bg-purple-600 text-white"
														: "bg-white text-gray-600 hover:bg-gray-50"}
												`}>
													<input
														type="radio"
														name="childGender"
														value="M"
														checked={form.childGender === "M"}
														onChange={handleChange}
														className="hidden"
													/>
													남자
												</label>
												<label className={`flex-1 text-center py-3 cursor-pointer transition font-medium border-l
													${form.childGender === "F"
														? "bg-purple-600 text-white"
														: "bg-white text-gray-600 hover:bg-gray-50"}
												`}>
													<input
														type="radio"
														name="childGender"
														value="F"
														checked={form.childGender === "F"}
														onChange={handleChange}
														className="hidden"
													/>
													여자
												</label>
											</div>
										</div>
										{formErrors.childGender && (
											<p className="text-red-500 text-xs mt-1 min-h-[16px]">
												{formErrors.childGender}
											</p>
										)}
									</div>
								</div>
							</div>
							<div className="mt-6 space-y-3">
								<div className="border rounded-lg p-4">
									<div className="flex items-center justify-between">
										<label className="flex items-center gap-3 flex-1 min-w-0 mr-2 cursor-pointer">
											<input
												type="checkbox"
												name="agreeTerms"
												checked={form.agreeTerms}
												onChange={handleChange}
												className="w-5 h-5 cursor-pointer appearance-none border border-gray-400 rounded shrink-0
												checked:bg-purple-600 checked:border-purple-600
												relative
												before:content-[''] before:absolute before:left-[6px] before:top-[2px]
												before:w-[5px] before:h-[10px]
												before:border-white before:border-r-2 before:border-b-2
												before:rotate-45 before:opacity-0
												checked:before:opacity-100"
											/>
											<span className="text-m sm:text-base text-gray-700 truncate min-w-0">
												이용약관 동의 (필수)
											</span>
										</label>
										<button
											type="button"
											onClick={() =>
												setOpenTerms(prev => ({ ...prev, terms: !prev.terms }))
											}
											className="text-xs text-gray-500 hover:text-gray-700 underline shrink-0"
										>
											{openTerms.terms ? "접기" : "보기"}
										</button>
									</div>
									{openTerms.terms && (
										<div className="mt-3 text-xs text-gray-600 bg-gray-50 p-3 rounded-md max-h-40 overflow-y-auto">
											<p>본 서비스는 회원가입 및 서비스 제공을 위해 아래와 같은 약관에 동의가 필요합니다.</p>
											<p className="mt-3">1. 회원은 본인의 정확한 정보를 입력해야 하며, 타인의 정보를 도용할 수 없습니다.</p>
											<p>2. 서비스 이용 중 관련 법령 및 공공질서를 준수해야 합니다.</p>
											<p>3. 회사는 서비스 제공을 위해 필요한 경우 서비스 내용을 변경하거나 중단할 수 있습니다.</p>
											<p>4. 회원의 부주의로 발생한 문제에 대해서는 회사가 책임을 지지 않습니다.</p>
											<p className="mt-3">위 내용을 충분히 이해하였으며, 이에 동의합니다.</p>
										</div>
									)}
								</div>
								<div className="border rounded-lg p-4">
									<div className="flex items-center justify-between">
										<label className="flex items-center gap-2 flex-1 min-w-0 mr-2 cursor-pointer">
											<input
												type="checkbox"
												name="agreePrivacy"
												checked={form.agreePrivacy}
												onChange={handleChange}
												className="w-5 h-5 cursor-pointer appearance-none border border-gray-400 rounded shrink-0
												checked:bg-purple-600 checked:border-purple-600
												relative
												before:content-[''] before:absolute before:left-[6px] before:top-[2px]
												before:w-[5px] before:h-[10px]
												before:border-white before:border-r-2 before:border-b-2
												before:rotate-45 before:opacity-0
												checked:before:opacity-100"
											/>
											<span className="text-m sm:text-base text-gray-700 truncate min-w-0">
												개인정보 수집 및 이용 동의 (필수)
											</span>
										</label>
										<button
											type="button"
											onClick={() =>
												setOpenTerms(prev => ({ ...prev, privacy: !prev.privacy }))
											}
											className="text-xs text-gray-500 hover:text-gray-700 underline shrink-0"
										>
											{openTerms.privacy ? "접기" : "보기"}
										</button>
									</div>
									{openTerms.privacy && (
										<div className="mt-3 text-xs text-gray-600 bg-gray-50 p-3 rounded-md max-h-40 overflow-y-auto">
											<p>회사는 회원가입 및 서비스 제공을 위해 아래와 같이 개인정보를 수집·이용합니다.</p>
											<p className="mt-3">1. 수집 항목</p>
											<p>- 부모/보호자: 이름, 연락처, 이메일</p>
											<p>- 자녀: 이름, 생년월일, 성별</p>
											<p className="mt-3">2. 수집 목적</p>
											<p>- 회원 식별 및 서비스 제공</p>
											<p>- 고객 문의 응대 및 관리</p>
											<p className="mt-3">3. 보유 및 이용 기간</p>
											<p>- 회원 탈퇴 시까지 보관 후 지체 없이 파기</p>
											<p className="mt-3">4. 동의 거부 권리</p>
											<p>- 개인정보 수집에 동의하지 않을 권리가 있으며, 동의하지 않을 경우 서비스 이용이 제한될 수 있습니다.</p>
											<p className="mt-3">위 내용을 충분히 이해하였으며, 이에 동의합니다.</p>
										</div>
									)}
								</div>
								<div className="border rounded-lg p-4">
									<div className="flex items-center justify-between">
										<label className="flex items-center gap-2 cursor-pointer">
											<input
												type="checkbox"
												name="agreePrivacy"
												checked={isAllChecked}
												onChange={handleAllAgree}
												className="w-5 h-5 cursor-pointer appearance-none border border-gray-400 rounded
												checked:bg-purple-600 checked:border-purple-600
												relative
												before:content-[''] before:absolute before:left-[6px] before:top-[2px]
												before:w-[5px] before:h-[10px]
												before:border-white before:border-r-2 before:border-b-2
												before:rotate-45 before:opacity-0
												checked:before:opacity-100"
											/>
											<span className="text-m sm:text-base text-gray-700">
												전체 동의
											</span>
										</label>
									</div>
								</div>
							</div>
							<button
								onClick={handleJoin}
								onKeyDown={handleKeyDown}
								disabled={joinMutation.isPending}
								className={`w-full py-3 rounded-lg font-semibold transition 
    								${joinMutation.isPending ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:opacity-90"}
								`}
							>
								{joinMutation.isPending ? "회원가입 중..." : "회원가입"}
							</button>
							{joinError && (
								<p className="text-red-500 text-sm mt-3 text-center">
									{joinError}
								</p>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

export default Join;